/**
 * Content Repository
 * Simplified for MVP - handles blog posts, stories, FAQ, legal docs
 */

import { Story, BlogPost, FAQCategory, LegalDoc } from '../domain/types';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  setDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  QueryConstraint,
  DocumentSnapshot,
  onSnapshot
} from "firebase/firestore";

const COLLECTIONS = {
  stories: 'stories',
  posts: 'blogPosts',
  faq: 'faq',
  legal: 'legal'
} as const;

class ContentRepository {
  // ===== STORIES =====
  subscribeToStories(callback: (stories: Story[]) => void): () => void {
    try {
      const q = query(
        collection(db, COLLECTIONS.stories),
        orderBy("createdAt", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const stories = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Story));
        callback(stories);
      }, (error) => {
        logger.error('[Content] Error subscribing to stories', error);
        callback([]);
      });
    } catch (error) {
      logger.error('[Content] Error setting up stories subscription', error);
      return () => {};
    }
  }

  async getStories(): Promise<Story[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.stories), 
        orderBy("createdAt", "desc")
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Story));
    } catch (error) {
      logger.error('[Content] Error fetching stories', error);
      return [];
    }
  }

  async addStory(story: Story): Promise<void> {
    try {
      if (story.id) {
        await setDoc(doc(db, COLLECTIONS.stories, story.id), story);
      } else {
        await addDoc(collection(db, COLLECTIONS.stories), story);
      }
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error adding story', error);
    }
  }

  async deleteStory(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.stories, id));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting story', error);
    }
  }

  async deleteAllStories(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.stories));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting all stories', error);
    }
  }

  async markStoryViewed(id: string): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.stories, id), { isViewed: true });
    } catch (error) {
      logger.error('[Content] Error marking story as viewed', error);
    }
  }

  async updateStoryStatus(id: string, status: 'active' | 'archived'): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.stories, id), { status });
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error updating story status', error);
    }
  }

  // ===== BLOG POSTS =====
  subscribeToPosts(callback: (posts: BlogPost[]) => void): () => void {
    try {
      const q = query(
        collection(db, COLLECTIONS.posts),
        where("status", "==", "active"),
        orderBy("createdAt", "desc")
      );
      return onSnapshot(q, (snapshot) => {
        const posts = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as BlogPost));
        callback(posts);
      }, (error) => {
        logger.error('[Content] Error subscribing to posts', error);
        callback([]);
      });
    } catch (error) {
      logger.error('[Content] Error setting up posts subscription', error);
      return () => {};
    }
  }

  async getAllPosts(
    cursor: DocumentSnapshot | null = null, 
    pageSize: number = 10, 
    status: 'active' | 'archived' = 'active'
  ): Promise<{ posts: BlogPost[], lastDoc: DocumentSnapshot | null }> {
    try {
      const constraints: QueryConstraint[] = [
        where("status", "==", status),
        orderBy("createdAt", "desc"),
        limit(pageSize)
      ];
      if (cursor) constraints.push(startAfter(cursor));

      const q = query(collection(db, COLLECTIONS.posts), ...constraints);
      const snap = await getDocs(q);
      const posts = snap.docs.map(d => ({ id: d.id, ...d.data() } as BlogPost));
      return { posts, lastDoc: snap.docs[snap.docs.length - 1] || null };
    } catch (error) {
      logger.error('[Content] Error fetching posts', error);
      return { posts: [], lastDoc: null };
    }
  }

  async savePost(post: Partial<BlogPost>): Promise<string> {
    const data = {
      ...post,
      status: post.status || 'active',
      updatedAt: new Date().toISOString()
    };

    try {
      if (post.id) {
        await setDoc(doc(db, COLLECTIONS.posts, post.id), data, { merge: true });
        CacheInvalidator.invalidateContent();
        return post.id;
      } else {
        const docRef = await addDoc(collection(db, COLLECTIONS.posts), {
          ...data,
          createdAt: new Date().toISOString(),
          likes: []
        });
        CacheInvalidator.invalidateContent();
        return docRef.id;
      }
    } catch (error) {
      logger.error('[Content] Error saving post', error);
      throw error;
    }
  }

  async deletePost(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.posts, id));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting post', error);
    }
  }

  async deleteAllPosts(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.posts));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting all posts', error);
    }
  }

  async togglePostReaction(postId: string, userId: string, reaction: 'like' | 'dislike'): Promise<void> {
    try {
      const postRef = doc(db, COLLECTIONS.posts, postId);
      const postDoc = await getDoc(postRef);
      if (!postDoc.exists()) return;

      const post = postDoc.data() as BlogPost;
      const likes = post.likes || [];
      const dislikeKey = `dislike_${userId}`;
      const likeKey = `like_${userId}`;

      let updatedLikes = [...likes];
      
      if (reaction === 'like') {
        if (!updatedLikes.includes(likeKey)) {
          updatedLikes = updatedLikes.filter(like => like !== dislikeKey);
          updatedLikes.push(likeKey);
        }
      } else {
        if (!updatedLikes.includes(dislikeKey)) {
          updatedLikes = updatedLikes.filter(like => like !== likeKey);
          updatedLikes.push(dislikeKey);
        }
      }

      await updateDoc(postRef, { likes: updatedLikes });
    } catch (error) {
      logger.error('[Content] Error toggling post reaction', error);
    }
  }

  async addPosts(posts: BlogPost[]): Promise<void> {
    try {
      const addPromises = posts.map(post => 
        setDoc(doc(db, COLLECTIONS.posts, post.id), post)
      );
      await Promise.all(addPromises);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error adding multiple posts', error);
    }
  }

  async toggleArchivePost(postId: string, isArchived: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.posts, postId), { 
        status: isArchived ? 'archived' : 'active' 
      });
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error toggling post archive', error);
    }
  }

  // ===== FAQ =====
  async getFaqs(): Promise<FAQCategory[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.faq));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as FAQCategory));
    } catch (error) {
      logger.error('[Content] Error fetching FAQs', error);
      return [];
    }
  }

  async saveFaq(faq: FAQCategory): Promise<void> {
    try {
      await setDoc(doc(db, COLLECTIONS.faq, faq.id), faq);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error saving FAQ', error);
    }
  }

  async deleteFaq(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.faq, id));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting FAQ', error);
    }
  }

  async deleteAllFaqs(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.faq));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting all FAQs', error);
    }
  }

  // ===== LEGAL =====
  async getLegalDocs(): Promise<LegalDoc[]> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.legal));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as LegalDoc));
    } catch (error) {
      logger.error('[Content] Error fetching legal docs', error);
      return [];
    }
  }

  async saveLegalDoc(docData: LegalDoc): Promise<void> {
    try {
      await setDoc(doc(db, COLLECTIONS.legal, docData.id), docData);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error saving legal doc', error);
    }
  }

  async deleteLegalDoc(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.legal, id));
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting legal doc', error);
    }
  }

  async deleteAllLegalDocs(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTIONS.legal));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      CacheInvalidator.invalidateContent();
    } catch (error) {
      logger.error('[Content] Error deleting all legal docs', error);
    }
  }
}

export const contentRepository = new ContentRepository();
